import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import {
  EnhancedAppointment,
  IAppointmentConfig,
  Timezone,
} from '@bb/domain/dist/types';
import {
  AppointmentType,
  BasicContact,
  HistoryEntry,
} from '@bb/domain/dist/types/Appointment';
import { Table, theme } from 'components';
import React from 'react';
import styled from 'styled-components';
import { formatActionTaken, featureEnabled } from 'utils';
import { isBefore } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { formatStatus } from '@app/helpers';

const HistorySection = ({
  appointment,
  listingAppointmentConfig,
  timezone,
}: {
  appointment: BuildJsonType<EnhancedAppointment>;
  listingAppointmentConfig: Pick<IAppointmentConfig, 'contacts'>;
  timezone: Timezone;
}) => {
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
    },
  ];

  const buildAction = (
    history: BuildJsonType<BuildJsonType<HistoryEntry>>,
    type: AppointmentType,
    coop: BuildJsonType<BasicContact>,
    allHistory: BuildJsonType<HistoryEntry>[] = [],
    _timezone: Timezone,
  ) => {
    const actionStr = formatActionTaken(
      history,
      type,
      coop,
      undefined,
      allHistory,
      _timezone,
    );

    if (
      typeof history.actionDetails !== 'string' &&
      history.actionDetails?.cancelledBy
    ) {
      actionStr.concat(
        ` ${history.status === 'cancel' ? 'Cancelled' : 'Denied'}`,
      );

      actionStr.concat(
        `${history.actionDetails.cancelledBy === 'unable' ? '-' : 'by'} ${
          history.actionDetails.cancelledBy
        }: ${history.actionDetails.reason || ''}`,
      );
    }
    return actionStr.toUpperCase();
  };

  const buildStatus = (
    history: BuildJsonType<BuildJsonType<HistoryEntry>>,
    type: AppointmentType,
    allHistory?: BuildJsonType<HistoryEntry>[],
  ) => {
    let formattedStatus = '';

    const contactsToMustCall = listingAppointmentConfig.contacts.reduce(
      (contacts, contact) => {
        if (contact.type === 'Concierge') return contacts;
        const rep = contact;
        if (
          rep &&
          Array.isArray(rep.phone) &&
          rep.phone.some(({ mustCall }: any) => mustCall)
        ) {
          contacts.push(rep);
        }
        return contacts;
      },
      [],
    );
    const mustCallContacts = contactsToMustCall.length > 0;

    if (
      history.actionTaken.includes('Auto Confirmed') &&
      history.isPending &&
      history.status === 'confirm' &&
      !mustCallContacts
    ) {
      formattedStatus = 'AUTO-CONFIRM (PENDING)';

      if (
        featureEnabled('nonAgentNotificationFeature') &&
        (appointment.coop.nonAgentId || appointment.coop.customAgentId)
      ) {
        formattedStatus = formatStatus(history.status).toUpperCase();

        const prevNonAgentNotified = allHistory?.some(
          entry =>
            entry.nonAgentNotified &&
            isBefore(new Date(entry.date), new Date(history.date)),
        );

        const typeName = appointment.coop.isNotAgent
          ? 'HOME INSPECTOR/APPRAISER'
          : 'CUSTOM AGENT';
        formattedStatus = formattedStatus.concat(
          `(${typeName} ${
            history.nonAgentNotified || prevNonAgentNotified ? '' : 'NOT'
          } NOTIFIED)`,
        );
      }
    } else if (
      history.actionTaken.includes('Auto Confirmed') &&
      history.status === 'confirm' &&
      mustCallContacts
    ) {
      formattedStatus = 'CONFIRM (MUST CALL)';
    }
    if (
      !(
        history.actionTaken.includes('Auto Confirmed') &&
        history.isPending &&
        history.status === 'confirm'
      )
    ) {
      formattedStatus = formatStatus(history.status).toUpperCase();
      if (
        history.isPending &&
        mustCallContacts &&
        history.status === 'confirm'
      ) {
        formattedStatus = formattedStatus.concat(' (MUST CALL)');
      } else if (
        history.isPending &&
        (!mustCallContacts || history.status !== 'confirm') &&
        history.conciergePending
      ) {
        formattedStatus = formattedStatus.concat(
          '(Pending Delivery to Concierge)',
        );
      } else if (
        featureEnabled('nonAgentNotificationFeature') &&
        (appointment.coop.nonAgentId || appointment.coop.customAgentId) &&
        ['confirm', 'cancel', 'deny'].includes(history.status) &&
        !history.conciergePending
      ) {
        const prevNonAgentNotified = allHistory?.some(
          entry =>
            entry.nonAgentNotified &&
            isBefore(new Date(entry.date), new Date(history.date)),
        );

        const typeName = appointment.coop.isNotAgent
          ? 'HOME INSPECTOR/APPRAISER'
          : 'CUSTOM AGENT';
        formattedStatus = formattedStatus.concat(
          `(${typeName} ${
            history.nonAgentNotified ||
            prevNonAgentNotified ||
            (appointment.coop.customAgentId &&
              appointment.coop.approvedNonAgent)
              ? ''
              : 'NOT'
          } NOTIFIED)`,
        );
      } else if (
        history.isPending &&
        (!mustCallContacts || history.status !== 'confirm') &&
        [
          'Home Inspection',
          'Appraisal',
          'Photographer',
          'Pest Control',
        ].includes(type) &&
        (!featureEnabled('nonAgentNotificationFeature') ||
          !appointment.coop.nonAgentId)
      ) {
        const typeName = {
          'Home Inspection': 'Home Inspector',
          Appraisal: 'Appraiser',
          Photographer: 'Photographer',
          'Pest Control': 'Pest Control',
        }[
          type as
            | 'Home Inspection'
            | 'Appraisal'
            | 'Photographer'
            | 'Pest Control'
        ];
        formattedStatus = formattedStatus.concat(
          `(Pending Delivery to ${typeName})`,
        );
      } else if (
        history.isPending &&
        (!mustCallContacts || history.status !== 'confirm') &&
        !history.conciergePending &&
        // @ts-ignore
        !['Home Inspection', 'Appraisal', 'Photographer'].includes(type) &&
        (!featureEnabled('nonAgentNotificationFeature') ||
          !appointment.coop.nonAgentId)
      ) {
        formattedStatus = formattedStatus.concat(' (PENDING)');
      }
    }
    return formattedStatus;
  };

  const data = appointment.history
    .map(history => ({
      date: format(
        utcToZonedTime(history.date, timezone),
        'MMM dd, yyyy hh:mm:ss aa',
      ),
      user: history.userName.toUpperCase(),
      status: buildStatus(history, appointment.type, appointment.history),
      action: buildAction(
        history,
        appointment.type,
        appointment.coop,
        appointment.history,
        timezone,
      ),
    }))
    .sort((a, b) => (new Date(a.date) > new Date(b.date) ? -1 : 1));
  return (
    <>
      <StyledTable
        data-cy="history-table"
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          total: appointment.history.length,
          pageSize: 5,
        }}
        columns={columns}
        dataSource={data}
      />
    </>
  );
};

export default HistorySection;

export const StyledTable = styled(Table)`
  margin-top: 15px;
  .ant-table {
    border: 1px solid ${theme.gray4};
    border-bottom: none;
  }

  .ant-table-thead .ant-table-cell {
    color: ${theme.gray7};
    font-weight: 600;
    text-transform: uppercase;
    padding: 10px 16px;
    background: white;
  }
`;
